/**
 * F5 — Per-stage floor entry form rework (v2).
 * - image gate via hasMedia(stage) (replaces hardcoded allow-array)
 * - floor-zero lock via isPlinth(stage) (replaces label comparison)
 * - write createConstructionEvent/updateConstructionEvent body:
 *   { buildingId, wbsStageId, floor, plannedDate, achievedDate }
 *   (adds plannedDate; floor nullable for non-FLOOR scope)
 * - handle 409 (duplicate event) with toast.error
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import usePrivilege from "../../hooks/usePrivilege";
import moment from "moment";
import { EditButton, OutlineButton, PrimaryButton, SecondaryButton } from "../Buttons";
import { Edit, Plus, Eye, ArrowLeft, Calendar } from "lucide-react";
import PaginationControls from "../PaginationControls";
import { getMediaUrl } from "../../utils/media";
import { useWbsStages, isPlinth, hasMedia, isFloorScoped } from "../../constants/wbsStages";
import { getConstructionEvents, createConstructionEvent, updateConstructionEvent, uploadConstructionEventMedia } from "../../services/wbs/index";

const customStyles = {
  menu: (provided, state) => ({
    ...provided,
    zIndex: 9999,
    width: "400px",
    maxHeight: "200px",
    overflowY: "auto",
    ...(state.placement === "top"
      ? { bottom: "100%", top: "auto", marginBottom: "2px", marginTop: "0" }
      : { top: "100%", bottom: "auto", marginTop: "2px", marginBottom: "0" }),
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "4px",
    border: "1px solid #ccc",
    backgroundColor: "white",
  }),
  control: (provided, state) => ({
    ...provided,
    zIndex: 2,
    backgroundColor: "white",
    border: "1px solid #ccc",
    borderRadius: "4px",
    boxShadow: state.isFocused ? "0 0 0 1px gray" : null,
    "&:hover": { borderColor: state.isFocused ? "gray" : "#aaa" },
  }),
  option: (provided, state) => ({
    ...provided,
    padding: "8px 12px",
    fontSize: "14px",
    color: state.isSelected ? "white" : "black",
    backgroundColor: state.isSelected ? "gray" : "white",
    "&:hover": { backgroundColor: state.isSelected ? "gray" : "#f0f0f0", cursor: "pointer" },
  }),
  singleValue: (provided) => ({ ...provided, fontSize: "14px", color: "black" }),
  placeholder: (provided) => ({ ...provided, fontSize: "14px", color: "#777" }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#aaa",
    "&:hover": { color: "#333" },
  }),
  indicatorSeparator: (provided) => ({ ...provided, display: "none" }),
};

function StatusTypeDetail({
  projectId,
  statusType,
  buildingOptions,
  onBack,
  refreshBuildingMetadata,
  phaseId,
  refreshProgressImages,
  selectedBuilding,
  onDataUpdate
}) {
  const [rows, setRows] = useState([]);
  const [floorOptions, setFloorOptions] = useState([]);
  const { hasPrivilege } = usePrivilege();
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const imageObjectUrlRef = useRef(null);

  // v2: catalog from /wbs/stages
  const { byId, loading: stagesLoading } = useWbsStages();

  // Resolve the current stage from statusType.value (wbsStageId)
  const currentStage = byId[statusType?.value];

  const getDisplayImageUrl = (source) => {
    if (!source) return null;
    if (source instanceof File || source instanceof Blob) return URL.createObjectURL(source);
    if (typeof source === "string") {
      if (source.startsWith("http://") || source.startsWith("https://") || source.startsWith("data:"))
        return source;
      return `data:image/jpeg;base64,${source}`;
    }
    return getMediaUrl(source);
  };

  // v2: image upload gated by hasMedia(stage) — no hardcoded array
  const isImageUploadAllowed = () => hasMedia(currentStage);

  // v2: plinth check by isPlinth(stage) — not label comparison
  const isPlinthStage = () => isPlinth(currentStage);

  // BUILDING/PROJECT-scoped stages (incl. plinth) have exactly ONE row per
  // building (floor IS NULL on the wire) — a single target + achieved date.
  const isSingleRowStage = () => !!currentStage && !isFloorScoped(currentStage);

  useEffect(() => {
    if (selectedBuilding) {
      setFloorOptions(selectedBuilding.floors || []);
    }
  }, [selectedBuilding]);

  useEffect(() => {
    if (selectedBuilding && statusType) {
      fetchStatusRows();
    }
  }, [selectedBuilding, statusType]);

  const fetchStatusRows = async () => {
    if (!projectId || !statusType || !selectedBuilding) return;

    setIsLoading(true);
    try {
      const params = { projectId: projectId, projectPhaseId: phaseId };
      // v2: getConstructionEvents returns ResponseConstructionEventData[]
      const response = await getConstructionEvents(params);
      const responseArray = Array.isArray(response) ? response : (response?.items || []);
      // v2: filter by wbsStage.wbsStageId (not statusType.statusTypeId)
      const filteredRows = responseArray.filter(row =>
        row.wbsStage?.wbsStageId === statusType.value &&
        row.building.buildingId === selectedBuilding.buildingId
      );

      const fetchedRows = filteredRows.map((row) => ({
        ...row,
        buildingId: row.building.buildingId,
        buildingName: row.building.buildingName,
        wbsStage: row.wbsStage,
        status: row.wbsStage?.wbsStageId,
        // v2: floor is nullable for BUILDING/PROJECT-scoped stages
        floor: row.floor !== null && row.floor !== undefined
          ? `${row.building.buildingId}-${row.floor}`
          : "",
        isNew: false,
        isSaving: false,
        // v2: media[] child; show first media item's progressImage
        imageUrl: row.media && row.media.length > 0 ? row.media[0].progressImage : null,
        isEditing: false,
        achievedDate: row.achievedDate ? moment(row.achievedDate).format("YYYY-MM-DD") : null,
        actualStartDate: row.actualStartDate ? moment(row.actualStartDate).format("YYYY-MM-DD") : null,
        targetStartDate: row.targetStartDate ? moment(row.targetStartDate).format("YYYY-MM-DD") : null,
        plannedDate: row.plannedDate ? moment(row.plannedDate).format("YYYY-MM-DD") : null,
      }));

      setRows(fetchedRows);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching construction events:", error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = rows.map((row, i) => (i === index ? { ...row, [field]: value } : row));
    setRows(updatedRows);
  };

  const handleAddRow = () => {
    if (!selectedBuilding) {
      toast.error("Please select a building before adding a row.");
      return;
    }

    // Single-row lock — BUILDING/PROJECT-scoped stages get exactly one row per building
    if (isSingleRowStage() && rows.length > 0) {
      toast.error(`${statusType?.label} already has a record for this building.`);
      return;
    }

    const newRow = {
      id: Date.now(),
      building: selectedBuilding.value,
      buildingId: selectedBuilding.buildingId,
      buildingName: selectedBuilding.label,
      status: statusType.value,
      // v2: plinth = floor 0
      floor: isPlinthStage() ? `${selectedBuilding.buildingId}-0` : "",
      imageUrl: null,
      achievedDate: null,
      plannedDate: null,
      isNew: true,
      isSaving: false,
      isEditing: true,
    };

    setRows([...rows, newRow]);
  };

  const handleSaveRow = async (index) => {
    const rowData = rows[index];
    const {
      buildingId, status, floor, achievedDate, actualStartDate, targetStartDate,
      plannedDate, constructionEventId, isNew, progressImage,
    } = rowData;

    let floorNumber = null;
    if (typeof floor === "object" && floor !== null) {
      floorNumber = parseInt(floor.value.split("-")[1], 10);
    } else if (floor) {
      floorNumber = parseInt(floor.split("-")[1], 10);
    }

    // v2: floor nullable for BUILDING/PROJECT-scoped stages
    const isFloorScope = isFloorScoped(currentStage);
    if (isFloorScope && floorNumber === null && !isPlinthStage()) {
      toast.error("Please select a floor.");
      return;
    }
    if (isPlinthStage()) floorNumber = 0;

    if (!buildingId) {
      toast.error("Please select a building.");
      return;
    }

    const achievedDateString = achievedDate ? moment(achievedDate).format("YYYY-MM-DD") : null;
    const plannedDateString = plannedDate ? moment(plannedDate).format("YYYY-MM-DD") : null;

    const updatedRows = rows.map((row, i) => (i === index ? { ...row, isSaving: true } : row));
    setRows(updatedRows);

    try {
      // v2: JSON body (not FormData) — { buildingId, wbsStageId, floor, plannedDate, achievedDate }
      if (actualStartDate && achievedDateString && achievedDateString < actualStartDate) {
        const reverted = rows.map((row, i) => (i === index ? { ...row, isSaving: false } : row));
        setRows(reverted);
        return toast.error("Achieved date cannot be before Actual Start.");
      }
      const body = {
        buildingId: buildingId,
        wbsStageId: status,
        floor: isFloorScope ? floorNumber : null,
        // Targets are managed via Define Targets — pass through unchanged.
        targetStartDate: targetStartDate || null,
        plannedDate: plannedDateString,
        actualStartDate: actualStartDate ? moment(actualStartDate).format("YYYY-MM-DD") : null,
        achievedDate: achievedDateString,
      };

      if (!isNew) {
        body.constructionEventId = constructionEventId;
      }

      let savedEvent;
      if (isNew) {
        savedEvent = await createConstructionEvent(body);
      } else {
        savedEvent = await updateConstructionEvent(body);
      }

      // Progress photo: upload AFTER the event exists (multipart companion endpoint).
      const eventId = savedEvent?.constructionEventId || constructionEventId;
      if (progressImage instanceof File && eventId && isImageUploadAllowed()) {
        try {
          await uploadConstructionEventMedia(eventId, progressImage);
        } catch (mediaErr) {
          toast.error("Status saved, but the photo upload failed — retry by editing the row.");
          console.error("Error uploading progress image:", mediaErr);
        }
      }

      const updatedRow = { ...rowData, isNew: false, isSaving: false, isEditing: false };
      const finalUpdatedRows = rows.map((row, i) => (i === index ? updatedRow : row));
      setRows(finalUpdatedRows);
      fetchStatusRows();
      toast.success("Status saved successfully");

      if (refreshBuildingMetadata) refreshBuildingMetadata();
      if (refreshProgressImages) refreshProgressImages();
      if (onDataUpdate) onDataUpdate();

    } catch (error) {
      const revertedRows = rows.map((row, i) => (i === index ? { ...row, isSaving: false } : row));
      setRows(revertedRows);
      // v2: handle 409 duplicate event with toast.error
      if (error?.response?.status === 409) {
        toast.error("A record for this building/stage/floor combination already exists.");
      } else {
        toast.error("Error saving status.");
      }
      console.error("Error saving status:", error);
    }
  };

  const handleEditRow = (index) => {
    const updatedRows = rows.map((row, i) => (i === index ? { ...row, isEditing: true } : row));
    setRows(updatedRows);
  };

  const handleCancelEdit = (index) => {
    const updatedRows = rows.map((row, i) => {
      if (i === index) {
        if (row.isNew) return null;
        return { ...row, isEditing: false };
      }
      return row;
    }).filter(row => row !== null);
    setRows(updatedRows);
  };

  const handleOpenImageViewer = (imageSource, imageName) => {
    if (imageObjectUrlRef.current) {
      URL.revokeObjectURL(imageObjectUrlRef.current);
      imageObjectUrlRef.current = null;
    }
    const resolvedUrl = getDisplayImageUrl(imageSource);
    if (imageSource instanceof File || imageSource instanceof Blob) {
      imageObjectUrlRef.current = resolvedUrl;
    }
    setSelectedImageUrl(resolvedUrl);
    setSelectedFileName(imageName);
    setIsImageViewerOpen(true);
  };

  const handleCloseImageViewer = () => {
    setIsImageViewerOpen(false);
    if (imageObjectUrlRef.current) {
      URL.revokeObjectURL(imageObjectUrlRef.current);
      imageObjectUrlRef.current = null;
    }
    setSelectedImageUrl(null);
    setSelectedFileName(null);
  };

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return rows.slice(start, end);
  }, [rows, currentPage, pageSize]);

  const pagination = useMemo(() => {
    const total = rows.length;
    const totalPages = Math.ceil(total / pageSize);
    return {
      page: currentPage,
      pageSize: pageSize,
      total: total,
      totalPages: totalPages,
      hasNext: currentPage < totalPages,
      hasPrevious: currentPage > 1
    };
  }, [rows.length, currentPage, pageSize]);

  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const handlePageSizeChange = (newPageSize) => { setPageSize(newPageSize); setCurrentPage(1); };

  return (
    <div className="h-[900px]">
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <button
              onClick={onBack}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label="Back"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold text-gray-800">
                {statusType?.label}{isSingleRowStage() ? " - Details" : " - Floor Details"}
              </h2>
              <div className="text-sm text-gray-500">
                {isSingleRowStage()
                  ? "Set the target date, achieved date and completion image for this building-wide stage."
                  : "View, add, or edit floor status details for the selected building and status type."}
              </div>
            </div>
          </div>
          {/* Single-row lock for BUILDING/PROJECT-scoped stages (incl. plinth) */}
          {hasPrivilege("canEdit") && !(isSingleRowStage() && rows.length > 0) && (
            <SecondaryButton
              onClick={handleAddRow}
              icon={<Plus className="mr-2 size-4" />}
              left
              className="px-4 py-2 text-sm"
            >
              {isSingleRowStage() ? "Add Status" : "Add Floor"}
            </SecondaryButton>
          )}
        </div>

        <div className="p-6 h-full mb-16">
          {isLoading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="mb-4">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Plus className="w-8 h-8 text-gray-400" />
                </div>
              </div>
              <p className="text-lg font-medium mb-2">No floor status records found</p>
              <p className="text-sm text-gray-400">Add a new floor status to get started</p>
            </div>
          ) : (
            <>
              <div className="h-full">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`${isImageUploadAllowed() ? "w-1/6" : "w-1/4"} px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>{isSingleRowStage() ? "Scope" : "Floor"}</th>
                      <th className={`${isImageUploadAllowed() ? "w-1/6" : "w-1/4"} px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Actual Start</th>
                      <th className={`${isImageUploadAllowed() ? "w-1/6" : "w-1/4"} px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Achieved Date</th>
                      {/* v2: image column gated by hasMedia(stage) */}
                      {isImageUploadAllowed() && (
                        <th className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                      )}
                      {hasPrivilege("canEdit") && (
                        <th className={`${isImageUploadAllowed() ? "w-1/4" : "w-1/4"} px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedRows.map((row, index) => {
                      const actualIndex = (currentPage - 1) * pageSize + index;
                      return (
                        <tr key={row.constructionEventId || row.id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className={`${isImageUploadAllowed() ? "w-1/6" : "w-1/4"} px-4 py-4 whitespace-nowrap text-sm text-gray-900`}>
                            {isPlinthStage() ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Plinth (Floor 0)
                              </span>
                            ) : isSingleRowStage() ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                Building-wide
                              </span>
                            ) : row.isEditing ? (
                              <Select
                                value={row.floor && typeof row.floor === "string"
                                  ? floorOptions.find((option) => option.value === row.floor)
                                  : row.floor}
                                onChange={(selectedOption) => handleInputChange(actualIndex, "floor", selectedOption.value)}
                                options={floorOptions}
                                className="text-sm"
                                styles={customStyles}
                                menuPlacement="top"
                                menuPortalTarget={document.body}
                              />
                            ) : (
                              <span className="text-sm font-medium">
                                {`Floor ${typeof row.floor === "string"
                                  ? row.floor.split("-")[1]
                                  : row.floor?.value?.split("-")[1] || row.floor || 0}`}
                              </span>
                            )}
                          </td>
                          <td className={`${isImageUploadAllowed() ? "w-1/6" : "w-1/4"} px-3 py-4 whitespace-nowrap text-sm text-gray-900`}>
                            {row.isEditing ? (
                              <div className="relative">
                                <DatePicker
                                  selected={row.actualStartDate ? new Date(row.actualStartDate) : null}
                                  onChange={(date) => handleInputChange(actualIndex, "actualStartDate", date ? moment(date).format("YYYY-MM-DD") : null)}
                                  dateFormat="yyyy/MM/dd"
                                  maxDate={new Date()}
                                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholderText="Select actual start"
                                />
                                <Calendar size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                              </div>
                            ) : (
                              <span className="text-sm">
                                {row.actualStartDate ? moment(row.actualStartDate).format("YYYY/MM/DD") : "—"}
                              </span>
                            )}
                          </td>
                          <td className={`${isImageUploadAllowed() ? "w-1/6" : "w-1/4"} px-3 py-4 whitespace-nowrap text-sm text-gray-900`}>
                            {row.isEditing ? (
                              <div className="relative">
                                <DatePicker
                                  selected={row.achievedDate ? new Date(row.achievedDate) : null}
                                  onChange={(date) => handleInputChange(actualIndex, "achievedDate", date ? moment(date).format("YYYY-MM-DD") : null)}
                                  dateFormat="yyyy/MM/dd"
                                  maxDate={new Date()}
                                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholderText="Select achieved date"
                                />
                                <Calendar size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                              </div>
                            ) : (
                              <span className="text-sm">
                                {row.achievedDate ? moment(row.achievedDate).format("YYYY/MM/DD") : "—"}
                              </span>
                            )}
                          </td>
                          {/* Target dates are managed via the Define Targets dialog — the row's
                              existing plannedDate is still sent on save so it is never clobbered. */}
                          {/* v2: image column gated by hasMedia(stage) */}
                          {isImageUploadAllowed() && (
                            <td className="w-2/5 px-6 py-4 text-sm text-gray-900">
                              {row.isEditing ? (
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    const file = e.target.files[0];
                                    handleInputChange(actualIndex, "progressImage", file);
                                  }}
                                  className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                              ) : (row.progressImage || row.imageUrl) ? (
                                <button
                                  onClick={() => handleOpenImageViewer(
                                    row.progressImage || row.imageUrl,
                                    `${statusType?.label}_Floor_${typeof row.floor === "string" ? row.floor.split("-")[1] : row.floor?.value?.split("-")[1] || row.floor || 0}`
                                  )}
                                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                  <Eye size={16} className="mr-2" />
                                  View
                                </button>
                              ) : (
                                <span className="text-sm text-gray-400">No Image</span>
                              )}
                            </td>
                          )}
                          {hasPrivilege("canEdit") && (
                            <td className={`${isImageUploadAllowed() ? "w-1/4" : "w-1/4"} px-6 py-4 whitespace-nowrap text-sm text-gray-900`}>
                              {row.isSaving ? (
                                <div className="flex items-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                  <span className="text-sm text-gray-500">Saving...</span>
                                </div>
                              ) : (
                                <div className="flex space-x-2">
                                  {row.isEditing ? (
                                    <>
                                      <PrimaryButton
                                        className="px-3 py-2 text-xs"
                                        onClick={() => handleSaveRow(actualIndex)}
                                      >
                                        Save
                                      </PrimaryButton>
                                      <OutlineButton
                                        className="px-3 py-1 text-xs"
                                        onClick={() => handleCancelEdit(actualIndex)}
                                      >
                                        Cancel
                                      </OutlineButton>
                                    </>
                                  ) : (
                                    <EditButton onClick={() => handleEditRow(actualIndex)} />
                                  )}
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rows.length > 0 && (
                <PaginationControls
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemName="floor statuses"
                  variant="list"
                />
              )}
            </>
          )}
        </div>
      </div>

      {isImageViewerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg max-w-4xl max-h-[90vh] w-[95vw] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{selectedFileName || "Image Preview"}</h3>
              <button onClick={handleCloseImageViewer} className="text-gray-400 hover:text-gray-600 transition-colors duration-200">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4 flex items-center justify-center">
              <img src={selectedImageUrl || ""} alt="Status Image" className="max-w-full max-h-full object-contain rounded-lg" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatusTypeDetail;
